import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const formatErr = (detail) => {
  if (!detail) return "Login failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || "Invalid input";
  return "Login failed";
};

const AdminLogin = () => {
  useDocumentTitle("Admin Login");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in.");
      const to = location.state?.from || "/admin";
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-[calc(100vh-160px)] bg-gradient-to-br from-white via-slate-50 to-sky-50/40 flex items-center justify-center px-4 py-16"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 hover:text-[#0B1B3D] mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to website
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="/brand/akyvia-logo.png"
              alt="Akyvia Sciences and Technologies"
              className="w-12 h-12 rounded-full object-contain ring-1 ring-slate-200 shadow-sm"
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Akyvia
              </div>
              <div className="font-display text-xl font-semibold text-[#0B1B3D]">
                Admin Console
              </div>
            </div>
          </div>

          <h1 className="font-display text-2xl font-semibold text-[#0B1B3D] tracking-tight">
            Sign in to continue
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Restricted area — administrators only.
          </p>

          <form onSubmit={onSubmit} className="mt-8 grid gap-5">
            <div>
              <Label htmlFor="al-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="al-email"
                  data-testid="admin-login-email-input"
                  type="email"
                  required
                  className="pl-9"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="admin@akyvia.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="al-pass">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="al-pass"
                  data-testid="admin-login-password-input"
                  type="password"
                  required
                  className="pl-9"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#0B1B3D] hover:bg-[#1E3A8A] disabled:opacity-60 text-white font-medium px-6 py-3.5 rounded-md transition-colors"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
